import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE_CONFIG } from "../data/mockData";
import { fetchActiveCategories, searchArticles } from "../services/api";
import { useAuth } from "./AuthContext";
import { Menu, X, Search, PenLine, CalendarClock, Users, LogIn, LogOut, BarChart3 } from "lucide-react";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [navCategories, setNavCategories] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const loadActiveCategories = async () => {
      try {
        const cats = await fetchActiveCategories();
        setNavCategories(cats);
      } catch (err) {
        console.error("Failed to load active categories:", err);
      }
    };
    loadActiveCategories();
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      {/* Top bar */}
      <div className="bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10">
            <span className="text-gray-400 text-xs font-medium tracking-wide">
              DIFFERENT VIEWS. SAME PASSION.
            </span>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Search size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={SITE_CONFIG.logo}
              alt={SITE_CONFIG.name}
              className="h-10 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-black text-gray-900 tracking-tight">
                ON THE COUNTER
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                isActive("/")
                  ? "text-[#1B7A3D] bg-green-50"
                  : "text-gray-700 hover:text-[#1B7A3D] hover:bg-gray-50"
              }`}
            >
              Home
            </Link>
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`px-3 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  isActive(`/category/${cat.id}`)
                    ? "text-[#1B7A3D] bg-green-50"
                    : "text-gray-700 hover:text-[#1B7A3D] hover:bg-gray-50"
                }`}
              >
                {cat.name}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  to="/contributors"
                  data-testid="contributors-btn"
                  className={`ml-1 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    isActive("/contributors")
                      ? "text-[#1B7A3D] bg-green-50"
                      : "text-gray-500 hover:text-[#1B7A3D] hover:bg-green-50"
                  }`}
                >
                  <Users size={15} />
                  <span className="hidden xl:inline">Contributors</span>
                </Link>
                <Link
                  to="/scheduled"
                  data-testid="scheduled-articles-btn"
                  className={`ml-1 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    isActive("/scheduled")
                      ? "text-indigo-600 bg-indigo-50"
                      : "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  <CalendarClock size={15} />
                  <span className="hidden xl:inline">Scheduled</span>
                </Link>
                <Link
                  to="/statistics"
                  data-testid="statistics-nav-btn"
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-all ${
                    location.pathname === "/statistics"
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  <BarChart3 size={15} />
                  <span className="hidden xl:inline">Statistics</span>
                </Link>
                <Link
                  to="/write"
                  data-testid="write-article-btn"
                  className="ml-2 flex items-center gap-1.5 px-4 py-2 bg-[#1B7A3D] hover:bg-[#15612F] text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  <PenLine size={15} />
                  Write
                </Link>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="logout-btn"
                  className="ml-1 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <LogOut size={15} />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="login-btn"
                className="ml-2 flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 hover:text-[#1B7A3D] hover:bg-green-50 rounded-lg transition-colors"
              >
                <LogIn size={15} />
                Admin
              </Link>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="border-t border-gray-100 bg-gray-50 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim().length >= 2) {
                  navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                  setSearchOpen(false);
                  setSearchQuery("");
                  setSuggestions([]);
                }
              }}
              className="relative"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                data-testid="header-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchQuery(val);
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  if (val.trim().length >= 2) {
                    searchTimerRef.current = setTimeout(async () => {
                      try {
                        const data = await searchArticles(val.trim(), 5);
                        setSuggestions(data.articles || []);
                      } catch { setSuggestions([]); }
                    }, 300);
                  } else {
                    setSuggestions([]);
                  }
                }}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
                autoFocus
              />
            </form>
            {/* Live suggestions dropdown */}
            {suggestions.length > 0 && (
              <div data-testid="search-suggestions" className="absolute left-0 right-0 mx-4 sm:mx-6 lg:mx-8 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-50">
                {suggestions.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    data-testid={`search-suggestion-${article.id}`}
                    onClick={() => { setSearchOpen(false); setSearchQuery(""); setSuggestions([]); }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <Search size={14} className="text-gray-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{article.title}</p>
                      {article.excerpt && (
                        <p className="text-xs text-gray-400 truncate">{article.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
                <button
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchOpen(false);
                    setSearchQuery("");
                    setSuggestions([]);
                  }}
                  className="w-full px-4 py-2.5 text-xs font-semibold text-[#1B7A3D] hover:bg-green-50 transition-colors text-center"
                >
                  View all results
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                isActive("/")
                  ? "text-[#1B7A3D] bg-green-50"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              Home
            </Link>
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                  isActive(`/category/${cat.id}`)
                    ? "text-[#1B7A3D] bg-green-50"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {cat.name}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  to="/contributors"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Users size={15} />
                  Contributors
                </Link>
                <Link
                  to="/scheduled"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                    isActive("/scheduled")
                      ? "text-indigo-600 bg-indigo-50"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <CalendarClock size={15} />
                  Scheduled
                </Link>
                <Link
                  to="/statistics"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                    isActive("/statistics")
                      ? "text-indigo-600 bg-indigo-50"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <BarChart3 size={15} />
                  Statistics
                </Link>
                <Link
                  to="/write"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 rounded-md text-sm font-semibold text-[#1B7A3D] hover:bg-green-50 transition-colors"
                >
                  Write Article
                </Link>
                <button
                  onClick={async () => { setMobileMenuOpen(false); await logout(); navigate("/"); }}
                  className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <LogIn size={15} />
                Admin Login
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
